/**
 * The premium composer: the trainer's message to Buddy, in the hero (board N) or docked under the
 * thread (board M), drawn as the P5 boards' pill (owner, 4 November 2026: `BuddyP5f-1-start`,
 * `BuddyP5d-3-rozmowa`, `BuddyP5-3-stany`), in the states they draw.
 *
 *   · ready     „+", the text, the plan's language (hero), the mic and the round ember send in one
 *               56 px row; focus lifts the whole box by 2 px and its edge brightens
 *   · writing   once the text runs past one line it takes the whole width and the controls step
 *               down to a row under it, until the field is emptied again
 *   · working   send becomes a neutral stop; nothing else changes. The thread's own line says what
 *               Buddy is doing (said once, there), and the field stays and takes typing; what is
 *               typed is sent once the turn ends (wave 1 queues nothing, see the canon README)
 *   · blocked   the limit is reached: flat, the placeholder names when Buddy is back, send refuses
 *
 * ── One field, whatever the layout and the state ────────────────────────────────────────────
 * The text area is never unmounted: not when Buddy starts working, and not when the hero becomes the
 * dock (its box's children are keyed, so the field keeps its node across the two layouts). Focus
 * therefore never falls to the page when a message goes, which is what a keyboard trainer relies on.
 *
 * ── A message is sent once ───────────────────────────────────────────────────────────────────
 * Enter sends, Shift+Enter breaks the line. The composed message gets its `requestId` on its first
 * send and keeps it until the server takes it, so a send repeated after a lost answer is the same
 * request; editing the words makes it a new message. A send in flight refuses a second one.
 *
 * The sheet tray under the box carries the owner's sentence and „Wrzuć arkusz" (board T), which
 * opens the import, as the „+" beside the text does. Where the host offers no import (`onSheet` is
 * `null`) the tray says „Wkrótce" and the „+" states it.
 *
 * ── Three layouts ────────────────────────────────────────────────────────────────────────────
 * The hero and the dock are the same pill (the hero also names the plan's language); the builder's
 * assistant panel (boards I, S) keeps the field and send in one box, the tray, and the hint row.
 *
 * ── Dictation (R57 to R62, `docs/design/ai-buddy-dictation-2026-10-13/`) ─────────────────────
 * The mic in the hero's row and in the dock, never in the panel or in a rejection's reason (R57).
 * Recording lays a lane over the controls in the same row — over the field too while it is one
 * line, beside nothing but the round action — and turns send into a red stop on a neutral round
 * (stop and insert; board „Buddy P5 · Stany", the owner's version), then a spinner; the tray
 * carries its one line of status. The text lands at the caret
 * the field held when dictation began (by pointer, shortcut, or Tab from the field to the mic), or
 * after what is there, through the editing command so Ctrl Z takes it back in one step, and
 * NOTHING IS SENT: Enter on the stop or in the field stops and inserts (on × it cancels), Escape cancels,
 * Ctrl M starts and stops, and only while focus is in the composer. The state, the microphone and
 * the request are `useDictation`'s; which state means what is `model/dictation.ts`'s.
 */

import clsx from 'clsx';
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import type { ComposerPhase } from '~/entities/ai-conversation';
import { useScreenMotionPreference } from '~/shared/design-system/motion';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useAutoGrow } from '../lib/useAutoGrow';
import { useDictation } from '../lib/useDictation';
import { writeInto } from '../lib/writeInto';
import { composerWraps } from '../model/composerWraps';
import {
  dictationAction,
  dictationAnnouncement,
  dictationEnter,
  dictationFocus,
  dictationHere,
  dictationInsertion,
  dictationLine,
  dictationMirror,
  dictationOffCopy,
  secondsLeft,
  type DictationLine,
  type FieldCaret,
} from '../model/dictation';
import { PlusGlyph, SendGlyph, SpinGlyph, StopGlyph } from './ChatGlyphs';
import { ComposerTray } from './ComposerTray';
import { DictationLane, CountdownArc } from './DictationLane';
import { DictationMic } from './DictationMic';
import { DictationMirror, type MirrorContent } from './DictationMirror';
import styles from './Composer.module.css';

export interface ComposerProps {
  readonly layout: 'hero' | 'dock' | 'panel';
  readonly phase: ComposerPhase;
  /** What Buddy is doing, drawn over the empty field while it works. */
  readonly statusLine: string;
  readonly placeholder: string;
  /** The words and the request they travel as; answers whether the server took them. */
  readonly onSend: (text: string, requestId: string) => Promise<boolean>;
  readonly onStop: () => void;
  /** The text area, for the cards to hand focus back to. */
  readonly fieldRef?: RefObject<HTMLTextAreaElement | null>;
  /** Opens the spreadsheet import; `null` where none is offered. */
  readonly onSheet: (() => void) | null;
  /** Escape in the field: what it takes back (a rejection's note), or `null` when nothing. */
  readonly onEscape?: (() => void) | null;
  /** The field is the reason for rejecting a proposal: no dictation there (R57). */
  readonly rejecting?: boolean;
  /** When Buddy is back, worded („o 14:20"), for the refusing mic; `null` when the server named no time. */
  readonly resetWhen?: string | null;
  /** Hears whether a dictation runs, for the start's example prompts to refuse while it does. */
  readonly onDictating?: (dictating: boolean) => void;
}

/** The text that just landed: where, what, and the field's value right after, for „Cofnij". */
interface Insertion {
  readonly start: number;
  readonly insert: string;
  readonly value: string;
}

/** How long the mic answers a landed text with a ✓ (board notes, „Inserted"). */
const ACK_MS = 1_200;

export function Composer({
  layout,
  phase,
  statusLine,
  placeholder,
  onSend,
  onStop,
  fieldRef,
  onSheet,
  onEscape = null,
  rejecting = false,
  resetWhen = null,
  onDictating,
}: ComposerProps) {
  const { t } = useTranslation();
  const contentLanguage = useContentLanguage();
  const reducedMotion = useScreenMotionPreference();
  const [text, setText] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const statusId = useId();
  const soonId = useId();
  const refusal = useId();
  const askingId = useId();
  const working = phase === 'working';
  const blocked = phase === 'blocked';
  // A send pressed while Buddy works is refused out loud, in the dock, until the turn ends.
  const [heldBack, setHeldBack] = useState(false);
  const [phaseSeen, setPhaseSeen] = useState(phase);
  if (phase !== phaseSeen) {
    setPhaseSeen(phase);
    if (phase !== 'working') setHeldBack(false);
  }

  const ownField = useRef<HTMLTextAreaElement>(null);
  const field = fieldRef ?? ownField;
  // Whether the controls stand under the text (`composerWraps`): a render settles what the text
  // alone decides, a measure what the text runs to.
  const [multiSeen, setMulti] = useState(false);
  const multi = composerWraps(multiSeen, { wraps: false, layout, text });
  if (multi !== multiSeen) setMulti(multi);
  useAutoGrow(field, text, (wraps) => {
    setMulti((previous) => composerWraps(previous, { wraps, layout, text }));
  });
  const mic = useRef<HTMLButtonElement>(null);
  const actionButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  // Where the caret stood when dictation began, and where focus goes back on a cancel.
  const [caret, setCaret] = useState<FieldCaret | null>(null);
  const caretAtPress = useRef<FieldCaret | null>(null);
  const origin = useRef<'field' | 'mic'>('mic');
  // The field's own input events while the dictated text is written are not the trainer typing.
  const writing = useRef(false);
  const [inserted, setInserted] = useState<Insertion | null>(null);
  const [entering, setEntering] = useState<Extract<MirrorContent, { kind: 'words' }> | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [undone, setUndone] = useState(false);
  // Ctrl M pressed while the mic refuses: the live region says why.
  const [offNoticed, setOffNoticed] = useState(false);
  // Focus is on the tray's act („Cofnij"): the line does not leave from under it.
  const [trayHeld, setTrayHeld] = useState(false);
  const composerRef = useRef<HTMLDivElement>(null);

  const dictation = useDictation({
    blocked,
    here: dictationHere(layout, rejecting),
    holdOutcome: trayHeld,
    onText: (transcript) => {
      const node = field.current;
      if (node === null) return;
      const landing = dictationInsertion(node.value, caret, transcript);
      writing.current = true;
      writeInto(node, landing.start, landing.end, landing.insert);
      writing.current = false;
      const landed = { start: landing.start, insert: landing.insert, value: node.value };
      setInserted(landed);
      setAcknowledged(true);
      setScrollTop(node.scrollTop);
      if (!reducedMotion) setEntering({ kind: 'words', ...landed });
    },
  });
  const { offer, state } = dictation;
  // A new state replaces the tray's line, and the act that held focus leaves with it.
  const [stateSeen, setStateSeen] = useState(state);
  if (state !== stateSeen) {
    setStateSeen(state);
    setTrayHeld(false);
  }
  const micShown = offer.kind !== 'none';
  const dictating = state.phase !== 'idle';
  // A layout effect, so the host's answer commits with the dictation's own state: one left pending
  // when the transcript is written makes React put the field's old value back over it.
  useLayoutEffect(() => {
    onDictating?.(dictating);
  }, [onDictating, dictating]);
  // The lane stays while a dictation runs whatever the offer does, so Escape and × still reach it.
  const laneShown = layout !== 'panel' && (micShown || dictating);
  // While the text is one line the lane covers the field as well; the field then carries nothing
  // of the dictation, and the lane says „przepisuję…" itself, as the dock always did.
  const covered = layout !== 'panel' && dictating && !multi;
  const drawnAs = covered ? 'dock' : layout;
  const left = state.phase === 'recording' ? secondsLeft(dictation.elapsed, dictation.maxSeconds) : null;

  useEffect(() => {
    if (!acknowledged) return;
    const timer = window.setTimeout(() => {
      setAcknowledged(false);
    }, ACK_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [acknowledged]);

  // Focus follows the dictation (`dictationFocus`), but only while it is in the composer or was
  // dropped with a control that left: a recording stopping itself never pulls focus from elsewhere.
  useEffect(() => {
    const target = dictationFocus(state, origin.current);
    if (target === null) return;
    const focused = document.activeElement;
    if (focused !== null && focused !== document.body && composerRef.current?.contains(focused) !== true) return;
    const node = target === 'action' ? actionButton.current : target === 'mic' ? (mic.current ?? field.current) : field.current;
    node?.focus({ preventScroll: true });
  }, [state, field]);

  const begin = (from: 'field' | 'mic') => {
    if (offer.kind !== 'live' || dictating) return;
    const node = field.current;
    origin.current = from;
    setCaret(from === 'field' && node !== null ? { start: node.selectionStart, end: node.selectionEnd } : caretAtPress.current);
    caretAtPress.current = null;
    setInserted(null);
    setEntering(null);
    setUndone(false);
    setOffNoticed(false);
    dictation.start();
  };

  const undo = () => {
    const node = field.current;
    // „Cofnij" leaves with its line: focus goes to the field first, never to the page.
    node?.focus({ preventScroll: true });
    dictation.settle();
    setEntering(null);
    if (node === null || inserted === null || node.value !== inserted.value) return;
    writing.current = true;
    writeInto(node, inserted.start, inserted.start + inserted.insert.length, '');
    writing.current = false;
    setInserted(null);
    setUndone(true);
  };

  const act = (action: NonNullable<DictationLine['action']>) => {
    switch (action) {
      case 'undo':
        undo();
        return;
      case 'retry':
        dictation.retry();
        return;
      case 'again':
      case 'tryAgain':
        dictation.settle();
        begin(origin.current);
    }
  };

  const send = () => {
    if (working && text.trim() !== '') setHeldBack(true);
    if (blocked || working || sending || dictating || text.trim() === '') return;
    const id = requestId ?? crypto.randomUUID();
    setRequestId(id);
    setSending(true);
    void onSend(text, id).then((taken) => {
      setSending(false);
      if (!taken) return;
      setText('');
      setRequestId(null);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // While a dictation runs the composer's own keys are its: the field neither sends nor takes back.
    if (dictating) return;
    if (event.key === 'Escape' && onEscape !== null) {
      event.stopPropagation();
      onEscape();
      return;
    }
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    send();
  };

  // Ctrl M starts and stops, Escape cancels, Enter stops and inserts and never sends — all only
  // while focus is in the composer. Escape goes no further, so it closes no panel and takes back no note.
  const onComposerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!micShown && !dictating) return;
    if (event.key.toLowerCase() === 'm' && event.ctrlKey && !event.altKey && !event.metaKey && !event.shiftKey) {
      event.preventDefault();
      if (offer.kind === 'off' && !dictating) setOffNoticed(true);
      else if (state.phase === 'idle') begin(event.target === field.current ? 'field' : 'mic');
      else if (state.phase === 'recording') dictation.stop();
      return;
    }
    if (!dictating) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      dictation.cancel();
      return;
    }
    if (event.key === 'Enter') {
      const on = event.target === actionButton.current ? 'action' : event.target === field.current ? 'field' : event.target instanceof HTMLButtonElement ? 'otherButton' : 'field';
      const enter = dictationEnter(state.phase, on);
      if (enter === 'native') return;
      event.preventDefault();
      if (enter === 'stop') dictation.stop();
    }
  };

  // Where the dictation will land, drawn over the hero's read-only field while it runs, and the
  // words entering once they have; the dock hides its field under the lane instead.
  const landingAt = dictationInsertion(text, caret, '').start;
  const mark = dictationMirror(drawnAs, state.phase, text !== '');
  const mirror: MirrorContent | null =
    entering !== null
      ? entering
      : mark === 'ghost'
        ? { kind: 'ghost', value: text, at: landingAt, label: t('web:aiPlan.dictation.transcribingField') }
        : mark === 'marker'
          ? { kind: 'marker', value: text, at: landingAt }
          : null;
  const fieldClass = layout === 'panel' ? styles.fieldPanel : styles.fieldPill;
  const listening = drawnAs === 'hero' && (state.phase === 'requesting' || state.phase === 'recording');

  const fieldNode = (
    <div
      key="field"
      className={clsx(styles.fieldWrap, layout !== 'panel' && styles.cellField, layout !== 'panel' && styles.fades, covered && styles.under)}
      inert={covered}
    >
      <textarea
        ref={field}
        className={clsx(styles.field, fieldClass, listening && styles.fieldListening, entering !== null && styles.fieldVeiled)}
        rows={layout === 'panel' ? 2 : 1}
        value={text}
        placeholder={state.phase === 'transcribing' ? '' : listening ? t('web:aiPlan.dictation.listening') : placeholder}
        aria-label={t('web:aiPlan.composer.label')}
        aria-describedby={working ? statusId : blocked ? refusal : undefined}
        readOnly={blocked || dictating}
        onChange={(event) => {
          setText(event.target.value);
          setRequestId(null);
          if (writing.current) return;
          // The trainer typed: the dictation's line gives the tray back, and the words stop entering.
          setEntering(null);
          setOffNoticed(false);
          if (state.phase === 'idle' && state.outcome !== null) dictation.settle();
        }}
        onScroll={(event) => {
          setScrollTop(event.currentTarget.scrollTop);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => {
          caretAtPress.current = null;
        }}
        onBlur={(event) => {
          // Tab from the field to the mic: the caret it left is where the words go (R58).
          if (event.relatedTarget === null || event.relatedTarget !== mic.current) return;
          caretAtPress.current = { start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd };
        }}
      />
      {mirror !== null && (
        <DictationMirror
          content={mirror}
          fieldClass={fieldClass}
          scrollTop={scrollTop}
          onEntered={() => {
            setEntering(null);
          }}
        />
      )}
    </div>
  );

  const sheet = (
    <button
      key="sheet"
      type="button"
      className={clsx(
        styles.icon,
        // Dimmed only where it refuses: while Buddy works or the limit is reached it still opens the
        // import, as the tray's „Wrzuć arkusz" beside it does.
        onSheet === null && styles.iconDim,
        onSheet !== null && styles.iconLive,
        styles.cellSheet,
        styles.fades,
        dictating && styles.under,
      )}
      aria-label={t('web:aiPlan.composer.addSheet')}
      aria-disabled={onSheet === null ? 'true' : undefined}
      aria-describedby={onSheet === null ? soonId : undefined}
      inert={dictating}
      onClick={onSheet ?? undefined}
    >
      <PlusGlyph className={styles.iconGlyph} />
    </button>
  );

  const offCopy = offer.kind === 'off' ? dictationOffCopy(offer.reason, resetWhen) : null;
  const offReason = offCopy === null ? null : t(offCopy.key, offCopy.values);
  const micButton = micShown && (
    <DictationMic
      key="mic"
      refusal={offReason}
      denied={dictation.micBlocked}
      acknowledged={acknowledged}
      under={dictating}
      className={clsx(styles.cellMic, styles.fades)}
      micRef={mic}
      onPointerDown={() => {
        const node = field.current;
        caretAtPress.current =
          node !== null && document.activeElement === node ? { start: node.selectionStart, end: node.selectionEnd } : null;
      }}
      onStart={() => {
        begin('mic');
      }}
    />
  );

  const lane = layout !== 'panel' && laneShown && (
    <DictationLane key="lane" layout={drawnAs === 'hero' ? 'hero' : 'dock'} dictation={dictation} reducedMotion={reducedMotion} onCancel={dictation.cancel} cancelRef={cancelButton} />
  );

  // The round action: send, stop while Buddy works, the red stop (stop and insert) while recording, a
  // spinner while transcribing. One button, its glyphs cross-fading in place.
  const kind = dictationAction(state.phase, working);
  const neutral = kind === 'waitingMic' || kind === 'transcribing';
  const action = (
    <span key="action" className={clsx(styles.actionWrap, layout !== 'panel' && styles.cellAction)}>
      <button
        ref={actionButton}
        type="button"
        className={clsx(styles.send, (kind === 'stop' || kind === 'insert') && styles.stop, neutral && styles.neutral, kind === 'send' && blocked && styles.sendOff)}
        aria-label={
          kind === 'insert' || kind === 'waitingMic'
            ? t('web:aiPlan.dictation.stop')
            : kind === 'transcribing'
              ? t('web:aiPlan.dictation.transcribingAction')
              : kind === 'stop'
                ? t('web:aiPlan.composer.stop')
                : blocked
                  ? t('web:aiPlan.composer.sendUnavailable')
                  : t('web:aiPlan.composer.send')
        }
        aria-disabled={neutral || (kind === 'send' && (blocked || sending)) ? 'true' : undefined}
        aria-describedby={kind === 'send' && blocked ? refusal : kind === 'waitingMic' ? askingId : undefined}
        onClick={kind === 'insert' ? dictation.stop : kind === 'stop' ? onStop : kind === 'send' ? send : undefined}
      >
        {kind === 'stop' ? (
          <StopGlyph className={styles.actionGlyph} />
        ) : (
          <SendGlyph className={clsx(styles.actionGlyph, kind !== 'send' && styles.actionGlyphOut)} />
        )}
        {laneShown && (
          <>
            <span className={clsx(styles.actionGlyph, styles.recordStop, kind !== 'insert' && kind !== 'waitingMic' && styles.actionGlyphOut)} aria-hidden="true" />
            <SpinGlyph className={clsx(styles.actionGlyph, kind === 'transcribing' ? styles.spin : styles.actionGlyphOut)} />
          </>
        )}
      </button>
      {laneShown && <CountdownArc dictation={dictation} />}
    </span>
  );

  const announcement = dictationAnnouncement(state, left, resetWhen);
  const said =
    offNoticed && offReason !== null
      ? offReason
      : undone
        ? t('web:aiPlan.dictation.say.undone')
        : announcement === null
          ? ''
          : t(announcement.key, announcement.values);

  return (
    <div
      ref={composerRef}
      className={clsx(styles.composer, layout === 'dock' && styles.dock, layout === 'panel' && styles.panel)}
      onKeyDown={onComposerKeyDown}
    >
      <div className={clsx(styles.box, multi && styles.multi, blocked && styles.boxOff, dictating && styles.lifted)}>
        {layout === 'panel'
          ? [fieldNode, action]
          : [
              sheet,
              fieldNode,
              layout === 'hero' && (
                <span key="language" className={clsx(styles.language, styles.cellLanguage, styles.fades, dictating && styles.under)}>
                  {t(`web:aiPlan.composer.contentLanguage.${contentLanguage}`)}
                </span>
              ),
              micButton,
              lane,
              action,
            ]}
      </div>
      <ComposerTray
        line={layout === 'panel' ? null : dictationLine(state, left, resetWhen)}
        onSheet={onSheet}
        onAct={act}
        onHold={setTrayHeld}
      />
      {layout === 'panel' && (
        <p className={styles.hint}>
          <span>{t('web:aiPlan.composer.keys')}</span>
          <span>{t('web:aiPlan.composer.planLanguage', { language: t(`web:aiPlan.composer.languageCode.${contentLanguage}`) })}</span>
        </p>
      )}
      {heldBack && <p className={styles.heldBack}>{t('web:aiPlan.composer.heldBack')}</p>}
      {/* The one announcement of what Buddy is doing: the thread's own lines are hidden from it. */}
      <p className={hidden.srOnly} role="status">
        {working ? statusLine : ''}
      </p>
      {/* The dictation's own, apart from Buddy's: never the clock, once per change of state. */}
      {laneShown && <LiveRegionAnnouncer message={said} />}
      {kind === 'waitingMic' && (
        <span id={askingId} className={hidden.srOnly}>
          {t('web:aiPlan.dictation.tray.asking')}
        </span>
      )}
      <span id={soonId} className={hidden.srOnly}>
        {t('web:aiPlan.composer.sheetSoon')}
      </span>
      <span id={statusId} className={hidden.srOnly}>
        {t('web:aiPlan.composer.workingNote')}
      </span>
      {blocked && (
        <span id={refusal} className={hidden.srOnly}>
          {placeholder}
        </span>
      )}
    </div>
  );
}
